import { CORE_LOADER_TYPE } from '@/components/core/Loader/Loader';
import Feed, { FEED_THEMES } from '@/components/features/feed/Feed';
import { createClient } from '@/jsonapi/createClient';
import { FEED_ACTION_TYPES } from '@/utils/constants';
import { getSafeLocale } from '@/utils/locale';
import { serializeCollectionResource, serializeSingleResource } from '@/utils/serilize-resource';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import useSWRInfinite from 'swr/infinite';
import { useClient } from '@/components/shared/utilities/AppProvider/AppProvider';
import { useCallback, useMemo } from 'react';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';
import { prependUnique } from '@/utils/prependUnique';
import { FeedLayout } from '@/components/shared/layouts/FeedLayout/FeedLayout';

type ExploreProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const ExplorePage: NextPage<ExploreProps> = ({ collectionData, collectionsData }) => {
	const { t } = useTranslation();
	const client = useClient();

	const {
		data: collections,
		error,
		setSize,
		size,
	} = useSWRInfinite(
		(pageIndex, previousPageData) => {
			if (previousPageData && !previousPageData?.links?.next) return null; // reached the end

			const pageNumber = pageIndex + 1;

			return [
				'collection/item',
				{
					include: 'favoriter',
					page: pageNumber,
					per_page: 3,
				},
			] as const;
		},
		async (key) => {
			const data = await client.findAll(...key);

			return serializeCollectionResource(data);
		},
		{
			fallbackData: [collectionsData],
		}
	);

	const { items, isLoadingMore, isReachingEnd } = useMemo(
		() => extractSWRInfiniteData(collections, error, size),
		[collections, error, size]
	);

	const itemsFiltered = useMemo(() => prependUnique(collectionData?.data, items), [items, collectionData]);

	const onLoadMore = useCallback(() => setSize(size + 1), [setSize, size]);

	return (
		<FeedLayout>
			<Feed
				headerActionType={FEED_ACTION_TYPES.CONTRIBUTE}
				onCloseTo="/explore"
				headerTitle={t('explore-collection')}
				headerSubtitle={t('theres-always-more-beneath-th')}
				theme={FEED_THEMES.COLLECTION}
				loaderType={CORE_LOADER_TYPE.PRIMARY}
				items={itemsFiltered}
				isReachingEnd={isReachingEnd}
				isLoadingMore={isLoadingMore}
				onLoadMore={onLoadMore}
			/>
		</FeedLayout>
	);
};

export const getServerSideProps: GetServerSideProps = async ({ locale, query, req }) => {
	const client = createClient({ locale, cookie: req.headers.cookie });

	const collectionDataResponse = await client.find('collection/item', query.id, { include: 'favoriter' });

	const collectionData = serializeSingleResource(collectionDataResponse);

	const collectionsResponse = await client.findAll('collection/item', {
		include: 'favoriter',
		page: 1,
		per_page: 3,
	});
	const collectionsData = serializeCollectionResource(collectionsResponse);

	return {
		props: {
			collectionData,
			collectionsData,
			...(await serverSideTranslations(getSafeLocale(locale), ['common'])),
		},
	};
};

export default ExplorePage;
